'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { SmartCaptcha } from '@yandex/smart-captcha'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { NewsletterCheckbox, useAssent } from '@/entities/assent'
import { fetchDiscountRequest, submitMarkdownMindboxRegister } from '@/entities/b24'
import { useUser } from '@/entities/user'

import { bitrixChat } from '@/shared/integrations/BitrixChat'
import { sendYandexMetricaEvent } from '@/shared/integrations/yandex-metric/utils/send-metric-event'
import { BASE_URL, YANDEX_CAPTCHA_KEY } from '@/shared/lib/const/env'
import { getCookie } from '@/shared/lib/utils/common/getCookie'
import { formatPrice } from '@/shared/lib/utils/price/format-price'
import { emailSchemaOptional, normalizeEmail } from '@/shared/lib/validation/email'
import { formatPhoneMasked, phoneSchema } from '@/shared/lib/validation/phone'
import { Button, Col, Input, RHFEmailInput, RHFPhoneInput, Row } from '@/shared/ui'
import { AgreementBlock } from '@/shared/ui/AgreementBlock/AgreementBlock'
import { FormSuccessMessage } from '@/shared/ui/FormSuccessMessage'
import { Image } from '@/shared/ui/Image'
import { TextArea } from '@/shared/ui/TextArea/TextArea'

import cl from './styles.module.scss'

const markdownFormSchema = z.object({
	NAME: z
		.string({ required_error: 'Введите имя' })
		.min(2, 'Имя должно содержать минимум 2 символа')
		.regex(/^(?!\s*$).+/, 'Имя не может состоять только из пробелов'),
	EMAIL: emailSchemaOptional,
	PHONE: phoneSchema,
	COMMENT: z
		.string()
		.optional()
		.transform(v => (!v?.trim() ? 'Не указан' : v))
})

type MarkdownFormValues = z.infer<typeof markdownFormSchema>

type MarkdownProduct = {
	KOD: string
	PRICE: number
	PART_NUMBER: string
	STOCK_CODE: string
	TITLE: string
	OLD_PRICE: number
	STOCK_NAME: string
	IMAGES?: string[]
}

export const MarkdownForm = (props: MarkdownProduct) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		control,
		watch
	} = useForm<MarkdownFormValues>({
		mode: 'onBlur',
		resolver: zodResolver(markdownFormSchema),
		defaultValues: {
			NAME: '',
			EMAIL: '',
			PHONE: '',
			COMMENT: ''
		}
	})

	const { KOD, STOCK_CODE, TITLE, PRICE, PART_NUMBER, OLD_PRICE, STOCK_NAME } = props

	const { user, isLoading: userInfoIsLoading } = useUser()
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const watchedValues = watch()
	const {
		showNewsletterCheckbox,
		isAccepted: isNewsletterAccepted,
		setIsAccepted: setIsNewsletterAccepted,
		checkConsent,
		saveConsent,
		hasConsent
	} = useAssent()

	const phone = watch('PHONE')

	useEffect(() => {
		checkConsent(phone)
	}, [phone])

	const [successMessageIsVisible, setSuccessMessageIsVisible] = useState(false)
	const [errorMessage, setErrorMessage] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [isDisabled, setIsDisabled] = useState(false)
	const [captcha, setCaptcha] = useState<string>('')

	useEffect(() => {
		if (!user) return

		setValue('NAME', user.first_name)
		setValue('PHONE', formatPhoneMasked(user.user_phone))

		if (user.user_email && user.user_email !== '0') {
			setValue('EMAIL', normalizeEmail(user.user_email))
		}
	}, [user, setValue])

	const getPageUrl = (): string => {
		const pathSearch = searchParams?.toString()
		const asPath = `${pathname || '/'}${pathSearch ? `?${pathSearch}` : ''}`
		const fallbackUrl = new URL(`https://www.iport.ru${asPath}`)

		if (typeof window === 'undefined') {
			return fallbackUrl.toString()
		}

		return window.location.href
	}

	const onSubmit = async (values: MarkdownFormValues) => {
		if (!captcha) return
		setIsLoading(true)
		const pageUrl = getPageUrl()

		const clientSourceCookie = getCookie('_client_source')
		const stockSendText = (STOCK_NAME || '')
			.replace(/<[^>]*>/g, ' ')
			.replace(/\s+/g, ' ')
			.trim()

		const formData = {
			NAME: values.NAME,
			EMAIL: values.EMAIL ?? '',
			PHONE: values.PHONE,
			COMMENTS: values.COMMENT,
			ITEM_CODE: KOD,
			ITEM_NAME: TITLE,
			ITEM_PN: PART_NUMBER,
			ITEM_PRICE: PRICE,
			STOCK_CODE: STOCK_CODE,
			SCRIPT_ID: `859386`,
			TYPE: `Markdown`,
			recaptcha_response: captcha
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} as any

		try {
			await fetchDiscountRequest(formData, clientSourceCookie)
			if (!hasConsent && isNewsletterAccepted) {
				await saveConsent(values.PHONE)
			}

			const normalizedPhone = values.PHONE.replace(/\)+|\s+|-+|\(+/gm, ``)

			void submitMarkdownMindboxRegister({
				NAME: values.NAME,
				PHONE: normalizedPhone,
				EMAIL: formData.EMAIL || '',
				TITLE: 'Уценённые товары',
				URL: pageUrl,
				SEND_TEXT: stockSendText,
				IS_INDIVIDUAL: 'true'
			}).catch(error => {
				console.error('Ошибка отправки markdown mindbox register:', error)
			})

			setSuccessMessageIsVisible(true)
			sendYandexMetricaEvent(`markdown-form`)
		} catch (err) {
			setIsLoading(false)
			setIsDisabled(true)
			setErrorMessage(true)
		}
	}

	const onChange = useCallback(
		(value: string) => {
			setCaptcha(value)
		},
		[setCaptcha]
	)

	return !successMessageIsVisible ? (
		<>
			<form onSubmit={handleSubmit(onSubmit)} className={cl.form}>
				<h2 className={cl.form__title}>Заявка на устройство</h2>
				<header className={cl.form__header}>
					{props.IMAGES?.[0] && (
						<div className={cl.image}>
							<Image src={BASE_URL + props.IMAGES[0]} width={100} height={100} alt={TITLE} />
						</div>
					)}
					<div className={cl.form__headerLeft}>
						<h3 className={cl.name}>{TITLE}</h3>
						<p className={cl.stock}>{STOCK_NAME}</p>
					</div>
					<div className={cl.form__headerRight}>
						{formatPrice(PRICE)}
						<span className={cl.oldprice}>{formatPrice(OLD_PRICE)}</span>
					</div>
				</header>
				<Row gutter={[8, 18]} style={{ width: `100%` }}>
					<Col xs={24} md={8}>
						<div className={cl.formField}>
							<Input label="Имя" isLoading={userInfoIsLoading} value={watchedValues.NAME || ''} {...register('NAME')} />
							{errors.NAME && <span className={cl.error}>{errors.NAME.message}</span>}
						</div>
					</Col>

					<Col xs={24} md={8}>
						<div className={cl.formField}>
							<RHFEmailInput
								name="EMAIL"
								control={control}
								error={errors.EMAIL}
								label="Email"
								required={false}
								isLoading={userInfoIsLoading}
							/>
						</div>
					</Col>

					<Col xs={24} md={8}>
						<div className={cl.formField}>
							<RHFPhoneInput name="PHONE" control={control} error={errors.PHONE} isLoading={userInfoIsLoading} />
						</div>
					</Col>

					<Col xs={24}>
						<div className={cl.formField}>
							<Controller
								name="COMMENT"
								control={control}
								render={({ field }) => (
									<TextArea
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										placeholder={`Укажите в комментарии,если хотите совершить покупку по программе обмена в Трейд-ин, оформить рассрочку или оплатить заказ частями в Сплит.`}
										maxLength={500}
									/>
								)}
							/>
						</div>
					</Col>
				</Row>

				{showNewsletterCheckbox && (
					<div className={cl.checkboxes}>
						<NewsletterCheckbox checked={isNewsletterAccepted} onChange={setIsNewsletterAccepted} />
					</div>
				)}

				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						marginBottom: '2rem',
						marginTop: '2rem'
					}}>
					<SmartCaptcha sitekey={YANDEX_CAPTCHA_KEY} onSuccess={onChange} language={'ru'} />
				</div>
				<Button
					id={null}
					type="submit"
					theme="primary"
					isLoading={isLoading}
					disabled={isDisabled || !captcha}
					className={cl.formButton}>
					Отправить
				</Button>

				{!!errorMessage && (
					<>
						Не удалось отправить заявку?
						<Button onClick={bitrixChat.open}>&nbsp;Напишите нам</Button>
					</>
				)}

				<div className={cl.agreement}>
					<AgreementBlock />
				</div>
			</form>
		</>
	) : (
		<>
			<FormSuccessMessage
				title="Ваша заявка отправлена"
				subtitle="В ближайшее время менеджер компании свяжется с вами"
			/>
		</>
	)
}
